'use client'

import React, { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import * as z from 'zod'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Badge } from '@/components/ui/badge'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { 
  Palette, 
  Upload, 
  Image as ImageIcon, 
  Save, 
  RefreshCw, 
  AlertCircle,
  CheckCircle2,
  Eye
} from 'lucide-react'
import { adminApi } from '@/lib/api'
import { notify } from '@/lib/notify'

const brandingSchema = z.object({
  applicationName: z.string().min(1, 'Application name is required').max(100),
  tagline: z.string().max(200).optional(),
  primaryColor: z.string().min(1, 'Primary color is required'),
  secondaryColor: z.string().min(1, 'Secondary color is required'),
  logoUrl: z.string().url().optional().or(z.literal('')),
})

type BrandingFormData = z.infer<typeof brandingSchema>

export default function AdminBranding() {
  const [logoFile, setLogoFile] = useState<File | null>(null)
  const [previewUrl, setPreviewUrl] = useState<string>('')
  const queryClient = useQueryClient()

  // Helper function to construct full image URL
  const getFullImageUrl = (logoUrl: string) => {
    if (!logoUrl) return ''
    if (logoUrl.startsWith('http')) return logoUrl // Already full URL
    // Construct full URL for relative paths
    const baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8011'
    const serverUrl = baseUrl.replace('/api', '') // Remove /api suffix if present
    return `${serverUrl}${logoUrl}`
  }

  const { data: branding, isLoading } = useQuery({
    queryKey: ['admin-branding'],
    queryFn: () => adminApi.get('/admin/branding').then((res) => res.data.data),
    retry: false,
  })

  const form = useForm<BrandingFormData>({
    resolver: zodResolver(brandingSchema),
    defaultValues: {
      applicationName: branding?.applicationName || 'redgeGate',
      tagline: branding?.tagline || 'SMS Gateway Platform',
      primaryColor: branding?.primaryColor || '#2563eb',
      secondaryColor: branding?.secondaryColor || '#64748b',
      logoUrl: branding?.logoUrl || '',
    },
  })

  // Update form when branding data is loaded
  React.useEffect(() => {
    if (branding) {
      form.reset({
        applicationName: branding.applicationName || 'redgeGate',
        tagline: branding.tagline || 'SMS Gateway Platform',
        primaryColor: branding.primaryColor || '#2563eb',
        secondaryColor: branding.secondaryColor || '#64748b',
        logoUrl: branding.logoUrl || '',
      })
      if (branding.logoUrl) {
        setPreviewUrl(getFullImageUrl(branding.logoUrl))
      }
    }
  }, [branding, form])

  const updateBrandingMutation = useMutation({
    mutationFn: (data: BrandingFormData) => adminApi.put('/admin/branding', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-branding'] })
      notify.success('Branding updated')
    },
    onError: (error: any) => {
      notify.error("Couldn't update branding", error.response?.data?.message || error.message)
    },
  })

  const uploadLogoMutation = useMutation({
    mutationFn: (file: File) => {
      const formData = new FormData()
      formData.append('logo', file)
      return adminApi.post('/admin/branding/upload-logo', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
    },
    onSuccess: (response) => {
      const logoUrl = response.data.data.logoUrl
      form.setValue('logoUrl', logoUrl)
      setPreviewUrl(getFullImageUrl(logoUrl))
      queryClient.invalidateQueries({ queryKey: ['admin-branding'] })
      notify.success('Logo uploaded')
    },
    onError: (error: any) => {
      notify.error("Couldn't upload the logo", error.response?.data?.message || error.message)
    },
  })

  const handleLogoUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        notify.error('Choose a PNG, JPG or SVG image')
        return
      }

      // Validate file size (2MB max)
      if (file.size > 2 * 1024 * 1024) {
        notify.error('Choose a file under 2 MB')
        return
      }

      setLogoFile(file)
      
      // Create preview URL
      const reader = new FileReader()
      reader.onload = (e) => {
        setPreviewUrl(e.target?.result as string)
      }
      reader.readAsDataURL(file)
    }
  }

  const handleUploadLogo = () => {
    if (logoFile) {
      uploadLogoMutation.mutate(logoFile)
    }
  }

  const onSubmit = (data: BrandingFormData) => {
    updateBrandingMutation.mutate(data)
  }

  const watchedValues = form.watch()

  if (isLoading) {
    return (
      <div className="p-8">
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="h-8 w-8 animate-spin text-blue-600" />
        </div>
      </div>
    )
  }

  return (
    <div className="p-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center space-x-3 mb-2">
            <Palette className="h-8 w-8 text-blue-600" />
            <h1 className="text-3xl font-bold text-gray-900">Application Branding</h1>
          </div>
          <p className="text-gray-600">
            Customize the appearance and branding of your SMS Gateway platform
          </p>
        </div>

        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Column - Settings */}
            <div className="lg:col-span-2 space-y-6">
              {/* Application Details */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <ImageIcon className="h-5 w-5" />
                    Application Details
                  </CardTitle>
                  <CardDescription>
                    Configure the basic information about your application
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="applicationName">Application Name</Label>
                      <Input
                        id="applicationName"
                        {...form.register('applicationName')}
                        placeholder="e.g., redgeGate"
                      />
                      {form.formState.errors.applicationName && (
                        <p className="text-sm text-red-600 mt-1">
                          {form.formState.errors.applicationName.message}
                        </p>
                      )}
                    </div>
                    <div>
                      <Label htmlFor="tagline">Tagline (Optional)</Label>
                      <Input
                        id="tagline"
                        {...form.register('tagline')}
                        placeholder="e.g., SMS Gateway Platform"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Colors */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Palette className="h-5 w-5" />
                    Color Scheme
                  </CardTitle>
                  <CardDescription>
                    Set the primary and secondary colors for your application
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="primaryColor">Primary Color</Label>
                      <div className="flex items-center space-x-3">
                        <Input
                          id="primaryColor"
                          type="color"
                          {...form.register('primaryColor')}
                          className="w-16 h-10 rounded"
                        />
                        <Input
                          {...form.register('primaryColor')}
                          placeholder="#2563eb"
                          className="flex-1"
                        />
                      </div>
                      {form.formState.errors.primaryColor && (
                        <p className="text-sm text-red-600 mt-1">
                          {form.formState.errors.primaryColor.message}
                        </p>
                      )}
                    </div>
                    <div>
                      <Label htmlFor="secondaryColor">Secondary Color</Label>
                      <div className="flex items-center space-x-3">
                        <Input
                          id="secondaryColor"
                          type="color"
                          {...form.register('secondaryColor')}
                          className="w-16 h-10 rounded"
                        />
                        <Input
                          {...form.register('secondaryColor')}
                          placeholder="#64748b"
                          className="flex-1"
                        />
                      </div>
                      {form.formState.errors.secondaryColor && (
                        <p className="text-sm text-red-600 mt-1">
                          {form.formState.errors.secondaryColor.message}
                        </p>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Logo Upload */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Upload className="h-5 w-5" />
                    Logo Upload
                  </CardTitle>
                  <CardDescription>
                    Upload your application logo (PNG, JPG, SVG - Max 2MB)
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label htmlFor="logo">Choose Logo File</Label>
                    <Input
                      id="logo"
                      type="file"
                      accept="image/*"
                      onChange={handleLogoUpload}
                      className="mt-1"
                    />
                  </div>
                  
                  {logoFile && (
                    <div className="flex items-center justify-between bg-blue-50 p-3 rounded-lg">
                      <span className="text-sm text-blue-800">
                        Selected: {logoFile.name}
                      </span>
                      <Button
                        type="button"
                        size="sm"
                        onClick={handleUploadLogo}
                        disabled={uploadLogoMutation.isPending}
                      >
                        {uploadLogoMutation.isPending ? (
                          <RefreshCw className="h-4 w-4 animate-spin mr-2" />
                        ) : (
                          <Upload className="h-4 w-4 mr-2" />
                        )}
                        Upload
                      </Button>
                    </div>
                  )}

                  <div>
                    <Label htmlFor="logoUrl">Logo URL (if not uploading)</Label>
                    <Input
                      id="logoUrl"
                      {...form.register('logoUrl')}
                      placeholder="https://example.com/logo.png"
                    />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Right Column - Preview */}
            <div className="space-y-6">
              {/* Preview */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Eye className="h-5 w-5" />
                    Preview
                  </CardTitle>
                  <CardDescription>
                    See how your branding will look
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {/* Logo Preview */}
                    <div className="flex items-center justify-center p-4 border-2 border-dashed border-gray-300 rounded-lg bg-gray-50">
                      {previewUrl ? (
                        <img
                          src={previewUrl}
                          alt="Logo Preview"
                          className="max-h-16 max-w-full object-contain"
                        />
                      ) : (
                        <div className="text-center text-gray-500">
                          <ImageIcon className="h-8 w-8 mx-auto mb-2" />
                          <p className="text-sm">No logo uploaded</p>
                        </div>
                      )}
                    </div>

                    {/* App Name Preview */}
                    <div className="text-center">
                      <h3 
                        className="text-lg font-bold"
                        style={{ color: watchedValues.primaryColor }}
                      >
                        {watchedValues.applicationName || 'redgeGate'}
                      </h3>
                      {watchedValues.tagline && (
                        <p 
                          className="text-sm"
                          style={{ color: watchedValues.secondaryColor }}
                        >
                          {watchedValues.tagline}
                        </p>
                      )}
                    </div>

                    {/* Color Swatches */}
                    <div className="space-y-2">
                      <div className="flex items-center space-x-2">
                        <div
                          className="w-4 h-4 rounded"
                          style={{ backgroundColor: watchedValues.primaryColor }}
                        ></div>
                        <span className="text-xs text-gray-600">Primary</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <div
                          className="w-4 h-4 rounded"
                          style={{ backgroundColor: watchedValues.secondaryColor }}
                        ></div>
                        <span className="text-xs text-gray-600">Secondary</span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Status */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm">Status</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      {branding ? (
                        <>
                          <CheckCircle2 className="h-4 w-4 text-green-600" />
                          <span className="text-sm text-green-600">Configured</span>
                        </>
                      ) : (
                        <>
                          <AlertCircle className="h-4 w-4 text-yellow-600" />
                          <span className="text-sm text-yellow-600">Not configured</span>
                        </>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-4 pt-6 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={() => form.reset()}
            >
              Reset
            </Button>
            <Button
              type="submit"
              disabled={updateBrandingMutation.isPending}
            >
              {updateBrandingMutation.isPending ? (
                <RefreshCw className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              Save Changes
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}
